<template>
  <AppSidebar>
    <div class="space-y-10 pb-12">
      <!-- Page Header -->
      <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div class="space-y-2">
            <div class="flex items-center gap-3 mb-1">
                <div class="w-2 h-2 bg-primary-500 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.5)] animate-pulse"></div>
                <span class="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest">Page Management</span>
            </div>
            <h1 class="text-4xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter sm:text-5xl" style="font-family: Poppins, sans-serif">
                Home <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-blue-500">Content Hub</span>
            </h1>
            <p class="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-widest leading-loose max-w-md">
                Manage your home page hero sections, services overview, and call-to-action modules.
            </p>
        </div>
      </div>

      <!-- Main Interface container -->
      <div class="bg-white dark:bg-gray-800/40 rounded-[40px] border border-gray-100 dark:border-gray-700/50 shadow-2xl shadow-gray-200/50 dark:shadow-none overflow-hidden backdrop-blur-xl">
        <!-- Premium Tabs -->
        <div class="border-b border-gray-100 dark:border-gray-800/50 px-8 py-2 bg-gray-50/30 dark:bg-gray-900/40">
          <nav class="flex gap-8 overflow-x-auto custom-scrollbar">
            <button v-for="tab in tabs" :key="tab.id" 
              @click="activeTab = tab.id"
              class="relative py-4 text-[10px] font-black uppercase tracking-widest transition-all duration-300 group whitespace-nowrap"
              :class="activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'">
              {{ tab.name }}
              <div class="absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-300"
                  :class="activeTab === tab.id ? 'w-full' : 'w-0 group-hover:w-1/2'"></div>
            </button>
          </nav>
        </div>

        <div class="p-8 sm:p-12">
          <!-- Loading State -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-24 space-y-4">
            <div class="relative w-16 h-16">
              <div class="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
              <div class="absolute inset-0 border-4 border-primary-500 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <span class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Loading Content...</span>
          </div>

          <div v-else class="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero'" class="max-w-4xl space-y-10">
              <div class="flex items-center gap-4 mb-2">
                  <div class="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                    <RocketLaunchIcon class="w-5 h-5 text-primary-500" />
                  </div>
                  <div>
                    <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Hero Configuration</h3>
                    <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Primary landing section settings</p>
                  </div>
              </div>

              <form @submit.prevent="saveSection('hero')" class="space-y-8">
                 <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Main Heading</label>
                    <input v-model="sections.hero.data.title" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                  <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Lead Text</label>
                    <input v-model="sections.hero.data.subtitle" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                </div>

                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Main Description</label>
                  <textarea v-model="sections.hero.data.description" rows="4"
                    class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Primary Button Text</label>
                    <input v-model="sections.hero.data.cta_primary" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-primary-600 dark:text-primary-400 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                  <div class="group">
                    <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Secondary Button Text</label>
                    <input v-model="sections.hero.data.cta_secondary" type="text" 
                        class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold tracking-widest text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                  </div>
                </div>

                <div class="flex justify-end pt-4">
                  <button type="submit" 
                    class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden font-sans">
                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    Initialize Section
                  </button>
                </div>
              </form>
            </div>

            <!-- Services Preview Tab -->
            <div v-if="activeTab === 'services_preview'" class="max-w-4xl space-y-10">
              <div class="flex items-center gap-4 mb-2">
                  <div class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
                    <CpuChipIcon class="w-5 h-5 text-blue-500" />
                  </div>
                   <div>
                    <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Services Architecture</h3>
                    <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Configure the services overview section</p>
                  </div>
              </div>

              <form @submit.prevent="saveSection('services_preview')" class="space-y-8">
                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Section Title</label>
                  <input v-model="sections.services_preview.data.title" type="text" 
                      class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                </div>
                <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Section Description</label>
                  <textarea v-model="sections.services_preview.data.description" rows="4"
                    class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                </div>
                <div class="flex justify-end pt-4">
                  <button type="submit" 
                    class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    Deploy Delta
                  </button>
                </div>
              </form>
            </div>

            <!-- Why Choose Us Tab -->
            <div v-if="activeTab === 'why_choose_us'" class="space-y-12">
              <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div class="flex items-center gap-4">
                    <div class="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                      <FingerPrintIcon class="w-5 h-5 text-emerald-500" />
                    </div>
                     <div>
                      <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Value Proposition</h3>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Manage unique service advantages</p>
                    </div>
                </div>
                <button @click="openFeatureModal()" 
                  class="inline-flex items-center px-6 py-3 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:scale-105 transition-all active:scale-95">
                  <PlusIcon class="w-4 h-4 mr-2" />
                  Add Feature
                </button>
              </div>

              <!-- Section Meta -->
              <div class="bg-gray-50/50 dark:bg-gray-900/20 p-8 rounded-[32px] border border-gray-100 dark:border-gray-700/50 max-w-4xl space-y-6">
                <h4 class="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1" style="font-family: Poppins, sans-serif">Section Core Configuration</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div class="group">
                    <label class="block text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Main Heading</label>
                    <input v-model="sections.why_choose_us.data.title" type="text" class="w-full px-4 py-3 bg-white dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-xl text-[11px] font-bold uppercase text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20" />
                  </div>
                  <div class="group">
                    <label class="block text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Short Description</label>
                    <input v-model="sections.why_choose_us.data.description" type="text" class="w-full px-4 py-3 bg-white dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-xl text-[11px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/20" />
                  </div>
                </div>
                <button @click="saveSection('why_choose_us')" class="text-[10px] font-black text-primary-500 uppercase tracking-widest hover:underline">Sync Core Configuration</button>
              </div>

              <!-- Features Grid -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="feature in whyChooseUsFeatures" :key="feature.id" 
                  class="group p-6 bg-white dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 rounded-[32px] transition-all hover:bg-gray-50 dark:hover:bg-gray-800/40 relative">
                  <div class="flex items-center justify-between mb-4">
                     <div class="text-[9px] font-bold uppercase tracking-widest" :class="feature.is_active ? 'text-emerald-500' : 'text-gray-400'">
                      {{ feature.is_active ? 'Active' : 'Inactive' }}
                    </div>
                    <div class="flex gap-1">
                       <button @click="moveFeatureUp(feature)" :disabled="isFirstFeature(feature)" class="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20"><ArrowUpIcon class="w-3 h-3"/></button>
                       <button @click="moveFeatureDown(feature)" :disabled="isLastFeature(feature)" class="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20"><ArrowDownIcon class="w-3 h-3"/></button>
                    </div>
                  </div>
                   <h5 class="text-[13px] font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-2" style="font-family: Poppins, sans-serif">{{ feature.title }}</h5>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed mb-6 line-clamp-2">{{ feature.description }}</p>
                  
                  <div class="flex justify-end gap-2 pt-4 border-t border-gray-50 dark:border-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="openFeatureModal(feature)" class="p-2 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all"><PencilSquareIcon class="w-4 h-4" /></button>
                    <button @click="deleteFeature(feature.id)" class="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all"><TrashIcon class="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            </div>

            <!-- CTA Tab -->
            <div v-if="activeTab === 'cta'" class="max-w-4xl space-y-10">
              <div class="flex items-center gap-4 mb-2">
                <div class="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
                  <RocketLaunchIcon class="w-5 h-5 text-purple-500" />
                </div>
                 <div>
                  <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Call To Action</h3>
                  <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Final conversion section settings</p>
                </div>
              </div>

              <form @submit.prevent="saveSection('cta')" class="space-y-8">
                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Heading</label>
                  <input v-model="sections.cta.data.title" type="text" 
                      class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-sm" />
                </div>
                 <div class="group">
                  <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 px-1">Description</label>
                  <textarea v-model="sections.cta.data.description" rows="4"
                    class="w-full px-6 py-5 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[32px] text-[12px] font-medium tracking-wider text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all resize-none shadow-sm leading-relaxed"></textarea>
                </div>
                <div class="flex justify-end pt-4">
                  <button type="submit" 
                    class="group relative inline-flex items-center px-10 py-4 bg-gray-900 dark:bg-primary-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl hover:scale-105 transition-all shadow-xl shadow-primary-500/20 active:scale-95 overflow-hidden">
                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>

            <!-- Statistics Tab -->
            <div v-if="activeTab === 'statistics'" class="space-y-12">
              <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div class="flex items-center gap-4">
                    <div class="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl">
                      <ChartBarIcon class="w-5 h-5 text-amber-500" />
                    </div>
                     <div>
                      <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Performance Metrics</h3>
                      <p class="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Display metrics to showcase growth</p>
                    </div>
                </div>
                <button @click="showStatModal = true; editingStatistic = null" 
                  class="inline-flex items-center px-6 py-3 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl hover:scale-105 transition-all active:scale-95">
                  <PlusIcon class="w-4 h-4 mr-2" />
                  Add Statistic
                </button>
              </div>

              <!-- Metrics Grid -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div v-for="stat in statistics" :key="stat.id" 
                  class="group p-8 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 rounded-[40px] text-center transition-all hover:bg-white dark:hover:bg-gray-800/50 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-none">
                   <div class="text-4xl font-bold text-primary-500 mb-2 tracking-tighter group-hover:scale-110 transition-transform">{{ stat.value }}</div>
                  <div class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-8">{{ stat.label }}</div>
                                    <div class="flex justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="editStatistic(stat)" class="text-[10px] font-bold text-primary-500 uppercase tracking-widest hover:underline">Edit</button>
                    <button @click="deleteStatistic(stat.id)" class="text-[10px] font-bold text-rose-500 uppercase tracking-widest hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals (Redesigned) -->
    <!-- Statistic Modal -->
    <div v-if="showStatModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeStatModal"></div>
        <div class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-md sm:w-full border border-white/20 dark:border-gray-800/50">
           <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter" style="font-family: Poppins, sans-serif">
              {{ editingStatistic ? 'Edit Statistic' : 'Add Statistic' }}
            </h3>
            <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">Display metrics for front-end visibility</p>
          </div>
          <form @submit.prevent="saveStatistic" class="p-10 space-y-6">
             <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Label Name</label>
              <input v-model="statForm.label" type="text" required class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm" />
            </div>
            <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Metric Value</label>
              <input v-model="statForm.value" type="text" required class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm" placeholder="e.g. 100+" />
            </div>
             <div class="flex justify-end gap-4 pt-4">
               <button type="button" @click="closeStatModal" class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all">Cancel</button>
               <button type="submit" class="px-8 py-4 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-all">Save Changes</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Feature Modal -->
    <div v-if="showFeatureModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        <div class="fixed inset-0 bg-gray-900/40 dark:bg-black/60 backdrop-blur-md transition-opacity" @click="closeFeatureModal"></div>
        <div class="relative inline-block bg-white dark:bg-gray-900 rounded-[40px] text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full border border-white/20 dark:border-gray-800/50">
           <div class="px-10 py-8 border-b border-gray-100 dark:border-gray-800/50">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-tighter" style="font-family: Poppins, sans-serif">
              {{ editingFeature ? 'Edit Feature' : 'Add Feature' }}
            </h3>
            <p class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-1">Proposition Differentiator Details</p>
          </div>
          <form @submit.prevent="saveFeature" class="p-10 space-y-6">
             <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Feature Title</label>
              <input v-model="featureForm.title" type="text" required class="w-full px-5 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-2xl text-[12px] font-bold uppercase tracking-widest text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm" />
            </div>
            <div class="group">
              <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Feature Description</label>
              <textarea v-model="featureForm.description" rows="3" required class="w-full px-6 py-4 bg-gray-50/50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-700/50 rounded-[28px] text-[12px] font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-4 focus:ring-primary-500/10 shadow-sm resize-none"></textarea>
            </div>
             <div class="flex items-center justify-center p-6 bg-gray-50/50 dark:bg-gray-900/10 rounded-2xl border border-gray-100 dark:border-gray-700/50">
              <label class="relative inline-flex items-center cursor-pointer group">
                <input type="checkbox" v-model="featureForm.is_active" class="sr-only peer">
                <div class="w-12 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
                <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Active</span>
              </label>
            </div>
             <div class="flex justify-end gap-4 pt-4">
               <button type="button" @click="closeFeatureModal" class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all">Cancel</button>
               <button type="submit" class="px-8 py-4 bg-primary-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-2xl shadow-xl shadow-primary-500/20 active:scale-95 transition-all">Save Feature</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { 
    RocketLaunchIcon, CpuChipIcon, FingerPrintIcon, 
    ChartBarIcon, PlusIcon, PencilSquareIcon, 
    TrashIcon, ArrowUpIcon, ArrowDownIcon 
} from '@heroicons/vue/24/outline'
import { useContentStore } from '@/stores/content'
import { useStatisticsStore } from '@/stores/statistics'
import { useWhyChooseUsStore } from '@/stores/whyChooseUs'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const contentStore = useContentStore()
const statisticsStore = useStatisticsStore()
const whyChooseUsStore = useWhyChooseUsStore()

// State
const activeTab = ref('hero')
const showStatModal = ref(false)
const editingStatistic = ref(null)
const showFeatureModal = ref(false)
const editingFeature = ref(null)
const loading = ref(true)

const tabs = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'services_preview', name: 'Services Preview' },
  { id: 'why_choose_us', name: 'Why Choose Us' },
  { id: 'cta', name: 'Call to Action' },
  { id: 'statistics', name: 'Statistics' }
]

const statForm = ref({
  label: '',
  value: ''
})

const featureForm = ref({
  title: '',
  description: '',
  is_active: true
})

// Computed
const rawSections = computed(() => contentStore.pageSections.home || [])
const sections = computed(() => {
  const map = {}
  rawSections.value.forEach(s => map[s.section] = JSON.parse(JSON.stringify(s)))
  
  // Defaults
  if (!map.hero) map.hero = { section: 'hero', data: { title: '', subtitle: '', description: '', cta_primary: '', cta_secondary: '' } }
  if (!map.services_preview) map.services_preview = { section: 'services_preview', data: { title: '', description: '' } }
  if (!map.why_choose_us) map.why_choose_us = { section: 'why_choose_us', data: { title: '', description: '' } }
  if (!map.cta) map.cta = { section: 'cta', data: { title: '', description: '', cta_primary: '', cta_secondary: '' } }
  
  return map
})

const statistics = computed(() => statisticsStore.statistics)
const whyChooseUsFeatures = computed(() => whyChooseUsStore.sortedFeatures)

// Methods
const saveSection = async (sectionName) => {
  const section = sections.value[sectionName]
  try {
    if (section.id) {
      await contentStore.updatePageSection(section.id, {
        page: 'home',
        section: sectionName,
        data: section.data
      })
    } else {
      await contentStore.createPageSection({
        page: 'home',
        section: sectionName,
        data: section.data
      })
    }
    // Refresh
    await contentStore.fetchPageSections('home')
  } catch (error) {
    console.error('Error saving section:', error)
  }
}

// Statistics CRUD
const openStatModal = (stat = null) => {
  editingStatistic.value = stat
  if (stat) {
    statForm.value = { ...stat }
  } else {
    statForm.value = { label: '', value: '' }
  }
  showStatModal.value = true
}

const closeStatModal = () => {
  showStatModal.value = false
  editingStatistic.value = null
  statForm.value = { label: '', value: '' }
}

const saveStatistic = async () => {
  if (editingStatistic.value) {
    await statisticsStore.updateStatistic(editingStatistic.value.id, statForm.value)
  } else {
    await statisticsStore.createStatistic(statForm.value)
  }
  closeStatModal()
}

const deleteStatistic = async (id) => {
  if (confirm('Are you sure you want to delete this statistic?')) {
    await statisticsStore.deleteStatistic(id)
  }
}

const editStatistic = (stat) => {
  openStatModal(stat)
}

// Why Choose Us Features CRUD
const openFeatureModal = (feature = null) => {
  editingFeature.value = feature
  if (feature) {
    featureForm.value = { ...feature }
  } else {
    featureForm.value = { title: '', description: '', is_active: true }
  }
  showFeatureModal.value = true
}

const closeFeatureModal = () => {
  showFeatureModal.value = false
  editingFeature.value = null
  featureForm.value = { title: '', description: '', is_active: true }
}

const saveFeature = async () => {
  try {
    if (editingFeature.value) {
      await whyChooseUsStore.updateFeature(editingFeature.value.id, featureForm.value)
    } else {
      await whyChooseUsStore.createFeature(featureForm.value)
    }
    closeFeatureModal()
  } catch (error) {
    console.error('Error saving feature:', error)
    alert('Error saving feature. Please try again.')
  }
}

const deleteFeature = async (id) => {
  if (confirm('Are you sure you want to delete this feature?')) {
    try {
      await whyChooseUsStore.deleteFeature(id)
    } catch (error) {
      console.error('Error deleting feature:', error)
      alert('Error deleting feature. Please try again.')
    }
  }
}

const isFirstFeature = (feature) => {
  const sorted = whyChooseUsFeatures.value
  return sorted.length > 0 && sorted[0].id === feature.id
}

const isLastFeature = (feature) => {
  const sorted = whyChooseUsFeatures.value
  return sorted.length > 0 && sorted[sorted.length - 1].id === feature.id
}

const moveFeatureUp = async (feature) => {
  const sorted = whyChooseUsFeatures.value
  const index = sorted.findIndex(f => f.id === feature.id)
  if (index > 0) {
    const features = [
      { id: feature.id, order: sorted[index - 1].order },
      { id: sorted[index - 1].id, order: feature.order }
    ]
    await whyChooseUsStore.updateFeaturesOrder(features)
  }
}

const moveFeatureDown = async (feature) => {
  const sorted = whyChooseUsFeatures.value
  const index = sorted.findIndex(f => f.id === feature.id)
  if (index < sorted.length - 1) {
    const features = [
      { id: feature.id, order: sorted[index + 1].order },
      { id: sorted[index + 1].id, order: feature.order }
    ]
    await whyChooseUsStore.updateFeaturesOrder(features)
  }
}

// Load data on mount
onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('home'),
      statisticsStore.fetchStatistics(),
      whyChooseUsStore.fetchAllFeatures()
    ])
  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})
</script>
